import { Group, Path, Rect, Text } from 'react-konva';
import type { CanvasTokens } from '../styles/tokens';
import type { AwarenessState } from './types';

interface Props {
  peers: Map<number, AwarenessState>;
  tokens: CanvasTokens;
  scale: number;
}

// Cursor body: simple chevron pointing up-left, like macOS / Figma. SVG path
// is in cursor-local coords (0,0 = pointer tip), filled with peer color.
const CURSOR_PATH =
  'M 0 0 L 0 14 L 4 11 L 7 17 L 9.5 16 L 6.5 10 L 12 9 Z';

// Renders one Konva Group per peer. Cursors are placed in world coords so they
// move with the canvas; their visual size is held constant by counter-scaling
// the Group with 1/viewport.scale so a cursor doesn't shrink as you zoom out.
export function RemoteCursors({ peers, tokens, scale }: Props) {
  const inverse = 1 / scale;
  const cursors: React.ReactNode[] = [];
  peers.forEach((state, clientID) => {
    if (!state.cursor) return;
    const color = tokens.cursors[state.user.colorIndex] ?? tokens.cursors[0]!;
    cursors.push(
      <PeerCursor
        key={clientID}
        x={state.cursor.x}
        y={state.cursor.y}
        scale={inverse}
        color={color}
        name={state.user.name}
      />,
    );
  });
  return <>{cursors}</>;
}

interface PeerCursorProps {
  x: number;
  y: number;
  scale: number;
  color: string;
  name: string;
}

function PeerCursor({ x, y, scale, color, name }: PeerCursorProps) {
  // Approximate name-pill width — Konva measures lazily and we don't need
  // pixel-perfect; the pill grows to fit `name.length * 6.5px + 16px padding`.
  const pillW = Math.max(name.length * 6.5 + 16, 36);
  const pillH = 18;
  return (
    <Group x={x} y={y} scaleX={scale} scaleY={scale} listening={false}>
      <Path
        data={CURSOR_PATH}
        fill={color}
        stroke="white"
        strokeWidth={1}
        shadowColor="black"
        shadowBlur={3}
        shadowOpacity={0.18}
        shadowOffsetY={1}
      />
      <Group x={14} y={14}>
        <Rect
          width={pillW}
          height={pillH}
          fill={color}
          cornerRadius={3}
          shadowColor="black"
          shadowBlur={3}
          shadowOpacity={0.15}
          shadowOffsetY={1}
        />
        <Text
          text={name}
          x={8}
          y={3}
          width={pillW - 16}
          height={pillH - 6}
          fontSize={11}
          fontFamily="Inter, sans-serif"
          fontStyle="500"
          fill="white"
          listening={false}
        />
      </Group>
    </Group>
  );
}
